import { Image } from 'lego-on-react';
import { escapeRegExp, startCase } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import {
    getScreenTypeIconByScreens,
    getScreenWeightCount
} from '../../service/IndoorOutdoorHelpers/IndoorOutdoorHelpers';
import {
    CheckBoxChangeData,
    Dictionary,
    DisplayListItem as ListItem,
    IndoorDictionary,
    IndoorDisplayListItem,
    MapType,
    OutdoorDisplayListItem
} from '../../typings/displayMap';
import {
    CheckboxDecorator,
    CheckboxDecoratorCheckedItem
} from '../CheckboxDecorator/CheckboxDecorator';
import { OutdoorTextHighlight } from '../OutdoorTextHighlight/OutdoorTextHighlight';
import { Shape2, Shape2Tone } from '../Shape2/Shape2';

import './DisplayListItem.css';

export interface DisplayListItemState {}

export interface DisplayListItemProps {
    searchText: string;
    mapType: MapType;
    platform: ListItem;
    highlighted?: boolean;
    checked?: boolean;
    dictionary: Dictionary;
    onCheck?: (data: CheckBoxChangeData) => void;
    onAddressClick?: (group: ListItem) => void;
}

export class DisplayListItem extends React.Component<
    DisplayListItemProps,
    DisplayListItemState
> {
    private block = 'display-list-item';

    render() {
        const { highlighted, platform } = this.props;
        const isDeleted = platform.screens.every(screen =>
            Boolean(screen.isDeleted)
        );

        return (
            <div
                className={Bem.cls({
                    block: this.block,
                    mods: {
                        highlighted: Boolean(highlighted)
                    }
                })}
            >
                <div
                    className={Bem.cls({
                        block: this.block,
                        elem: 'content',
                        mods: {
                            deleted: isDeleted
                        }
                    })}
                >
                    {this.getContent()}
                </div>
            </div>
        );
    }

    private getContent = () => {
        switch (this.props.mapType) {
            case MapType.indoor:
                return this.getIndoorContent();
            case MapType.outdoor:
                return this.getOutdoorContent();
            default:
                throw Error('Несуществующий тип карты');
        }
    };

    private getOutdoorContent = () => {
        const { searchText } = this.props;

        const platform = this.props.platform as OutdoorDisplayListItem;
        const regexp = new RegExp(escapeRegExp(searchText), 'ig');

        return (
            <>
                {this.getCheckbox()}
                <div className={Bem.cls(this.block, 'title')}>
                    <OutdoorTextHighlight
                        text={platform.caption}
                        search={regexp}
                        searchValue={searchText}
                    />
                </div>
                <div
                    onClick={this.onAddressClick}
                    className={Bem.cls(this.block, 'address')}
                    title={platform.address}
                >
                    <OutdoorTextHighlight
                        text={platform.address}
                        search={regexp}
                        searchValue={searchText}
                    />
                </div>
                <div className={Bem.cls(this.block, 'row')}>
                    <span className={Bem.cls(this.block, 'operator-name')}>
                        <OutdoorTextHighlight
                            text={platform.operatorName}
                            search={regexp}
                            searchValue={searchText}
                        />
                    </span>
                    {platform.gid && (
                        <>
                            <span> ∙ </span>
                            <span className={Bem.cls(this.block, 'gid')}>
                                <OutdoorTextHighlight
                                    text={platform.gid}
                                    search={regexp}
                                    searchValue={searchText}
                                />
                            </span>
                        </>
                    )}
                </div>
            </>
        );
    };

    private getIndoorContent = () => {
        const { searchText } = this.props;

        const dictionary = this.props.dictionary as IndoorDictionary;
        const platform = this.props.platform as IndoorDisplayListItem;

        const regexp = new RegExp(escapeRegExp(searchText), 'ig');

        const facilityType =
            dictionary.facility && dictionary.facility[platform.facilityType];
        const zoneCategory =
            dictionary.zone && dictionary.zone[platform.zoneCategory];

        return (
            <>
                {this.getStats(platform)}
                {this.getCheckbox()}
                <div className={Bem.cls(this.block, 'title')}>
                    <OutdoorTextHighlight
                        text={platform.caption}
                        search={regexp}
                        searchValue={searchText}
                    />
                </div>
                <div
                    onClick={this.onAddressClick}
                    className={Bem.cls(this.block, 'address')}
                    title={platform.address}
                >
                    <OutdoorTextHighlight
                        text={platform.address}
                        search={regexp}
                        searchValue={searchText}
                    />
                </div>
                <div className={Bem.cls(this.block, 'zone')}>
                    {startCase(facilityType) + ' · ' + startCase(zoneCategory)}
                </div>
                {/*{platform.gid && (*/}
                {/*<div className={Bem.cls(this.block, 'gid')}>*/}
                {/*<OutdoorTextHighlight*/}
                {/*text={platform.gid}*/}
                {/*search={regexp}*/}
                {/*searchValue={searchText}*/}
                {/*/>*/}
                {/*</div>*/}
                {/*)}*/}
            </>
        );
    };

    private onAddressClick = () => {
        const { onAddressClick, platform } = this.props;

        if (onAddressClick) {
            onAddressClick(platform);
        }
    };

    private getStats = (platform: IndoorDisplayListItem) => {
        return (
            <div className={Bem.cls(this.block, 'stats')}>
                {platform.imageLink && (
                    <Image
                        url={platform.imageLink}
                        cls={Bem.cls(this.block, 'image')}
                    />
                )}
                <div className={Bem.cls('stats', 'platforms-count')}>
                    {getScreenWeightCount(platform.screens)}
                </div>
                <Shape2
                    cls={Bem.cls(this.block, 'screens-icon')}
                    width={16}
                    height={16}
                    tone={Shape2Tone.grey}
                    icon={getScreenTypeIconByScreens(platform.screens)}
                />
            </div>
        );
    };

    private getCheckbox = () => {
        return (
            <div className={Bem.cls(this.block, 'checkbox-area')}>
                <CheckboxDecorator
                    cls={Bem.cls('checkbox-area', 'checkbox')}
                    theme={'normal'}
                    view={'default'}
                    size={'m'}
                    onChecked={this.onCheck}
                    checked={this.props.checked}
                />
            </div>
        );
    };

    private onCheck = (
        event: React.KeyboardEvent<HTMLElement>,
        checkboxData: CheckboxDecoratorCheckedItem
    ) => {
        const { onCheck, platform } = this.props;

        if (onCheck) {
            onCheck({
                id: platform.id,
                regionId: platform.regionId,
                checked: checkboxData.checked
            });
        }
    };
}
